/**
 * Direct Message composer (Pass F2).
 *
 * Sends a single email or SMS to one recipient via the same provider
 * layer as campaigns (Microsoft Graph for email, INTALK for SMS).
 * The audit row lands in the same `emails`/`sms_messages` table with
 * campaign_id = NULL, so the sent message appears in Communication
 * History right next to campaign sends.
 *
 * Students never carry their own email/phone — selecting a student
 * resolves the linked parent as the outbound recipient (F1a semantic).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Radio, Stack,
  TextField, Tooltip, Typography, FormControlLabel,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import useUser from "../../../Core/hook/useUser";
import { Privileges } from "../../../Core/Enum/enum";
import { useNotifier } from "../../../Core/Notifier";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../../Course/shared/moduleSx";
import { smsSegments } from "../../Settings/CommunicationTemplates/previewUtils";
import {
  DirectChannel, DirectClassification, DirectRecipientType,
  IRecipientRow, ITemplateRow, IPreviewResult,
  listTemplates, previewDirect, searchRecipients,
  sendDirectEmail, sendDirectSms,
} from "../../../Api/directMessageApi";

const RECIPIENT_TABS: Array<{
  key: DirectRecipientType; label: string; icon: React.ReactElement; note: string;
}> = [
  { key: "student", label: "Student", icon: <SchoolOutlinedIcon />,           note: "Delivered to the linked parent/guardian contact." },
  { key: "parent",  label: "Parent",  icon: <FamilyRestroomOutlinedIcon />,   note: "Delivered directly to the parent/guardian." },
  { key: "staff",   label: "Staff",   icon: <BadgeOutlinedIcon />,            note: "Delivered to the staff member's on-file contact." },
  { key: "admin",   label: "Admin",   icon: <AdminPanelSettingsOutlinedIcon />, note: "Delivered to the admin user's email. Admins have no primary phone." },
];

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44, borderRadius: "10px", bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
} as const;

const DirectMessagePage: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { userHasRight, isSystemAdmin } = useUser();
  const canSendTransactional = isSystemAdmin || userHasRight(Privileges.DirectCommunicationSendTransactional);

  // Composer state.
  const [recipientType, setRecipientType] = useState<DirectRecipientType>("parent");
  const [channel, setChannel]             = useState<DirectChannel>("email");
  const [classification, setClassification] = useState<DirectClassification>("transactional");
  const [recipient, setRecipient]         = useState<IRecipientRow | null>(null);
  const [searchText, setSearchText]       = useState("");
  const debouncedSearch                   = useDebounce(searchText, 300);
  const [recipientOptions, setRecipientOptions] = useState<IRecipientRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [templates, setTemplates] = useState<ITemplateRow[]>([]);
  const [templateId, setTemplateId] = useState<number | "">("");

  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");

  const [preview, setPreview]         = useState<IPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending]         = useState(false);

  const smsInfo = useMemo(() => (channel === "sms" ? smsSegments(body) : null), [body, channel]);

  // Idempotency key regenerated per composed message.
  const idemKey = useMemo(() => "dm-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8), [
    recipient?.id, channel, subject, body,
  ]);

  // ── Recipient search (server-side, debounced) ────────────────────
  useEffect(() => {
    if (! debouncedSearch) { setRecipientOptions([]); return; }
    setSearchLoading(true);
    searchRecipients(recipientType, debouncedSearch, 20)
      .then((rows) => setRecipientOptions(rows))
      .catch((e) => showErrorMessage(e?.message || "Recipient search failed."))
      .finally(() => setSearchLoading(false));
  }, [debouncedSearch, recipientType, showErrorMessage]);

  // Clear recipient when the type changes (rows are shaped differently).
  useEffect(() => {
    setRecipient(null); setSearchText(""); setRecipientOptions([]);
  }, [recipientType]);

  // ── Template list per channel ────────────────────────────────────
  useEffect(() => {
    listTemplates(channel)
      .then((rows) => setTemplates(rows))
      .catch((e) => showErrorMessage(e?.message || "Failed to load templates."));
    setTemplateId("");
  }, [channel, showErrorMessage]);

  // When a template is picked, populate subject/body.
  const applyTemplate = (id: number | "") => {
    setTemplateId(id);
    if (id === "") return;
    const t = templates.find((r) => r.id === id);
    if (! t) return;
    if (channel === "email") setSubject(t.subject ?? "");
    setBody(t.body);
  };

  // ── Channel availability derived from recipient ──────────────────
  const emailAvailable = Boolean(recipient?.email);
  const phoneAvailable = Boolean(recipient?.phone);
  const channelAvailable = channel === "email" ? emailAvailable : phoneAvailable;

  // ── Preview ──────────────────────────────────────────────────────
  const openPreview = async () => {
    if (! recipient) { showErrorMessage("Select a recipient first."); return; }
    setPreviewLoading(true);
    try {
      const p = await previewDirect({
        channel,
        recipient: { recipient_type: recipientType, recipient_id: recipient.id },
        subject: channel === "email" ? subject : undefined,
        body,
      });
      setPreview(p);
      setPreviewOpen(true);
    } catch (e: any) {
      showErrorMessage(e?.message || "Preview failed.");
    } finally { setPreviewLoading(false); }
  };

  // ── Send ─────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (! recipient)                                      return "Select a recipient.";
    if (channel === "email" && ! emailAvailable)          return "This recipient has no email address on file.";
    if (channel === "sms"   && ! phoneAvailable)          return "This recipient has no mobile number on file.";
    if (channel === "email" && ! subject.trim())          return "Email subject is required.";
    if (! body.trim())                                    return "Message body is required.";
    if (classification === "transactional" && ! canSendTransactional)
      return "You do not have permission to send transactional direct messages.";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { showErrorMessage(err); return; }
    setSending(true);
    try {
      const args = {
        recipient: { recipient_type: recipientType, recipient_id: recipient!.id },
        classification,
        idempotency_key: idemKey,
      };
      const result = channel === "email"
        ? await sendDirectEmail({ ...args, subject, body })
        : await sendDirectSms({   ...args, body });
      showMessage(`${channel === "email" ? "Email" : "SMS"} sent to ${result.recipient} — status: ${result.status}.`);
      setConfirmOpen(false);
      // Reset composer.
      setBody(""); setSubject(""); setTemplateId(""); setPreview(null);
    } catch (e: any) {
      showErrorMessage(e?.message || "Send failed.");
    } finally { setSending(false); }
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 44, height: 44, display: "flex", alignItems: "center",
                     justifyContent: "center", borderRadius: 2,
                     bgcolor: tint12(B13.primary), color: B13.primary }}>
            <SendOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em" }}>Direct Message</Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2 }}>
              One-off email or SMS to a single recipient. Delivered via Microsoft 365 / INTALK.
              Every send appears in Communication History.
            </Typography>
          </Box>
          <Tooltip title="Open Communication History">
            <IconButton onClick={() => navigate("/communication/history")}
              sx={{ width: 40, height: 40, borderRadius: "10px",
                    border: `1px solid ${B13.line}` }}
            ><PreviewOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "360px 1fr" }, gap: 2 }}>
        {/* Left column — Recipient + channel + template */}
        <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
          <Typography sx={{ ...B13TypeScale.h3, mb: 1.5 }}>Recipient</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
            {RECIPIENT_TABS.map((r) => {
              const active = recipientType === r.key;
              return (
                <Box key={r.key} component="button" type="button" onClick={() => setRecipientType(r.key)}
                  sx={{
                    px: 1.5, py: 0.75, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 0.75,
                    border: `2px solid ${active ? B13.primary : B13.line}`,
                    borderRadius: "10px",
                    bgcolor: active ? tint12(B13.primary) : "#fff",
                    color:   active ? B13.primary        : B13.ink,
                    fontSize: 13, fontWeight: 700,
                  }}
                >
                  {React.cloneElement(r.icon, { fontSize: "small" })}
                  {r.label}
                </Box>
              );
            })}
          </Stack>
          <Autocomplete
            fullWidth size="small"
            options={recipientOptions}
            value={recipient}
            onChange={(_, v) => setRecipient(v)}
            inputValue={searchText}
            onInputChange={(_, v) => setSearchText(v)}
            filterOptions={(x) => x /* server-side */}
            getOptionLabel={(o) => o.display_name ?? ""}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={searchLoading}
            noOptionsText={debouncedSearch ? "No matches." : "Type to search…"}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{option.display_name}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: B13Ink.ink2 }}>
                    {option.email && <>📧 {option.email}</>}
                    {option.email && option.phone && " · "}
                    {option.phone && <>📱 {option.phone}</>}
                    {! option.email && ! option.phone && <em>No email or phone on file</em>}
                  </Typography>
                  {option.note && (
                    <Typography sx={{ fontSize: 11, color: B13Ink.ink3, mt: 0.25 }}>{option.note}</Typography>
                  )}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search by name, email, phone…" sx={filterFieldSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Typography sx={{ fontSize: 12, color: B13Ink.ink3, mt: 1 }}>
            {RECIPIENT_TABS.find((r) => r.key === recipientType)?.note}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ ...B13TypeScale.h3, mb: 1 }}>Channel</Typography>
          <Stack direction="row" spacing={1}>
            {(["email", "sms"] as DirectChannel[]).map((c) => {
              const Icon = c === "email" ? EmailOutlinedIcon : SmsOutlinedIcon;
              const active = channel === c;
              const disabled = c === "email" ? recipient !== null && !emailAvailable
                                             : recipient !== null && !phoneAvailable;
              return (
                <Box key={c} component="button" type="button"
                  onClick={() => !disabled && setChannel(c)}
                  disabled={disabled}
                  sx={{
                    flex: 1, p: 1.5, cursor: disabled ? "not-allowed" : "pointer",
                    border: `2px solid ${active ? B13.primary : B13.line}`,
                    borderRadius: 2, opacity: disabled ? 0.5 : 1,
                    bgcolor: active ? tint12(B13.primary) : "#fff",
                    color:   active ? B13.primary        : B13.ink,
                    display: "flex", alignItems: "center", gap: 1,
                  }}
                >
                  <Icon fontSize="small" />
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                      {c === "email" ? "Email" : "SMS"}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: B13Ink.ink2 }}>
                      {c === "email"
                        ? (emailAvailable || !recipient ? "Rich HTML" : "No email available")
                        : (phoneAvailable || !recipient ? "160-char text" : "No phone available")}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ ...B13TypeScale.h3, mb: 1 }}>Template</Typography>
          <TextField select fullWidth size="small" value={templateId}
            onChange={(e) => applyTemplate(e.target.value === "" ? "" : Number(e.target.value))}
            sx={filterFieldSx}
          >
            <MenuItem value="">Start blank</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}{t.is_default ? " · default" : ""}</MenuItem>
            ))}
          </TextField>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ ...B13TypeScale.h3, mb: 1 }}>Classification</Typography>
          <Stack spacing={0.5}>
            <FormControlLabel
              control={<Radio size="small" checked={classification === "marketing"}
                onChange={() => setClassification("marketing")} />}
              label={<Typography sx={{ fontSize: 13 }}>Marketing — subject to consent + unsubscribe</Typography>}
            />
            <FormControlLabel
              control={<Radio size="small" checked={classification === "transactional"}
                onChange={() => setClassification("transactional")} disabled={!canSendTransactional} />}
              label={<Typography sx={{ fontSize: 13,
                color: canSendTransactional ? "inherit" : B13Ink.ink3 }}>
                Transactional — bypasses marketing suppression
                {! canSendTransactional && " (needs DirectCommunication_SendTransactional)"}
              </Typography>}
            />
          </Stack>
        </Box>

        {/* Right column — Message body */}
        <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
          <Typography sx={{ ...B13TypeScale.h3, mb: 1.5 }}>Message</Typography>

          {channel === "email" ? (
            <Stack spacing={1.5}>
              <TextField fullWidth required label="Subject" value={subject}
                onChange={(e) => setSubject(e.target.value)} sx={filterFieldSx} />
              <Box sx={{
                "& .ql-toolbar":   { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
                "& .ql-container": { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, minHeight: 260 },
              }}>
                <ReactQuill theme="snow" value={body} onChange={setBody}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ align: [] }],
                      ["link"],
                      ["clean"],
                    ],
                  }}
                />
              </Box>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <TextField fullWidth required multiline minRows={8}
                label="SMS body" value={body} onChange={(e) => setBody(e.target.value)}
                sx={filterFieldSx}
              />
              {smsInfo && (
                <Stack direction="row" spacing={1}>
                  <Chip size="small" label={`${smsInfo.chars} / ${smsInfo.perSegment}`}
                    sx={{ height: 22, fontSize: 11, bgcolor: "#F1F5F9" }} />
                  <Chip size="small" label={`${smsInfo.segments} SMS`}
                    sx={{ height: 22, fontSize: 11, fontWeight: 700,
                      bgcolor: smsInfo.segments > 1 ? "#FEF3C7" : "#DCFCE7",
                      color:   smsInfo.segments > 1 ? "#92400E" : "#15803D" }} />
                  <Chip size="small" label={smsInfo.encoding === "unicode" ? "Unicode" : "GSM-7"}
                    sx={{ height: 22, fontSize: 11, bgcolor: "#F1F5F9" }} />
                </Stack>
              )}
            </Stack>
          )}

          {classification === "marketing" && (
            <Alert severity="info" icon={false} sx={{ mt: 2 }}>
              Marketing sends automatically include the unsubscribe footer/link. Do not add one manually.
            </Alert>
          )}
          {recipient && ! channelAvailable && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {channel === "email" ? "No email address available for this recipient."
                                   : "No mobile number available for this recipient."}
            </Alert>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={openPreview}
              disabled={!recipient || !channelAvailable || previewLoading}
              sx={{ textTransform: "none", borderRadius: "10px" }}
              startIcon={<PreviewOutlinedIcon />}
            >{previewLoading ? "Rendering…" : "Preview"}</Button>
            <Button variant="contained"
              onClick={() => {
                const err = validate();
                if (err) { showErrorMessage(err); return; }
                setConfirmOpen(true);
              }}
              disabled={!recipient || !channelAvailable || sending}
              sx={{ textTransform: "none", borderRadius: "10px", bgcolor: B13.primary,
                    "&:hover": { bgcolor: B13.primary }, fontWeight: 700 }}
              startIcon={<SendOutlinedIcon />}
            >{channel === "email" ? "Send Email" : "Send SMS"}</Button>
          </Stack>
        </Box>
      </Box>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Preview</DialogTitle>
        <DialogContent>
          {preview && (
            <Stack spacing={2}>
              <Alert severity={preview.unresolved.length ? "warning" : "info"} icon={false}>
                Recipient: <strong>{preview.recipient.display_name}</strong>
                {" · "}
                {channel === "email" ? preview.recipient.email : preview.recipient.phone}
                {preview.unresolved.length > 0 && (
                  <div style={{ marginTop: 6, color: "#B91C1C" }}>
                    Unresolved variables: {preview.unresolved.map((v) => "{{" + v + "}}").join(", ")}
                  </div>
                )}
              </Alert>
              {channel === "email" ? (
                <>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3,
                                      textTransform: "uppercase", letterSpacing: 0.6 }}>Subject</Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 0.5 }}>
                      {preview.subject || "(no subject)"}
                    </Typography>
                  </Box>
                  <Box sx={{ border: `1px solid ${B13.line}`, borderRadius: 2, p: 2,
                             bgcolor: "#FAFBFC", maxHeight: 400, overflow: "auto" }}
                       dangerouslySetInnerHTML={{ __html: preview.body }} />
                </>
              ) : (
                <Box sx={{ maxWidth: 320, mx: "auto", border: `1px solid ${B13.line}`,
                           borderRadius: 3, p: 2, bgcolor: "#FAFBFC" }}>
                  <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{preview.body}</Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send confirmation */}
      <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm {channel === "email" ? "email" : "SMS"} send</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ fontSize: 14 }}>
            <Row label="Recipient" value={recipient?.display_name ?? "—"} />
            <Row label="Address"   value={(channel === "email" ? recipient?.email : recipient?.phone) ?? "—"} />
            <Row label="Channel"   value={channel.toUpperCase()} />
            <Row label="Classification" value={classification} />
            {channel === "email" && <Row label="Subject" value={subject || "—"} />}
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3,
                                textTransform: "uppercase", letterSpacing: 0.6 }}>Preview</Typography>
              <Box sx={{ border: `1px solid ${B13.line}`, borderRadius: 2, p: 1.5, mt: 0.5,
                         bgcolor: "#FAFBFC", maxHeight: 200, overflow: "auto", fontSize: 13 }}>
                {channel === "email"
                  ? <Box dangerouslySetInnerHTML={{ __html: body }} />
                  : <Typography sx={{ whiteSpace: "pre-wrap" }}>{body}</Typography>}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={sending}
            startIcon={<SendOutlinedIcon />}
            sx={{ textTransform: "none", bgcolor: B13.primary,
                  "&:hover": { bgcolor: B13.primary } }}
          >
            {sending ? "Sending…" : channel === "email" ? "Send Email" : "Send SMS"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 1 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3,
                      textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: B13.ink }}>{value}</Typography>
  </Box>
);

export default DirectMessagePage;
